// Planning du personnel : poste de chaque employé par jour, congés et absences, présence réelle, besoin en main-d'œuvre.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, Checkbox, Chip, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, IconButton, InputAdornment,
  Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Tooltip, Typography,
} from '@mui/material';
import {
  AddOutlined, ChevronLeft, ChevronRight, ContentCopyOutlined, FileDownloadOutlined, SaveOutlined, SearchOutlined, SettingsOutlined, TodayOutlined,
} from '@mui/icons-material';
import { Panel } from '../../components/ui';
import { planningAPI } from '../../services/api';
import { apiError } from '../../utils/format';
import { ecrireExcel } from '../../utils/excel';
import { tokens } from '../../theme';
import { aujourdhui, jourCourt, lundiDe, plusJours, semaineLabel } from './common';

function ReglagesDialog({ open, param, onClose, onSaved }) {
  const [f, setF] = useState(null);
  const [nouveau, setNouveau] = useState('');
  const [error, setError] = useState('');
  useEffect(() => { if (open && param) { setF({ ...param, productivite: param.productivite ? String(param.productivite).replace('.', ',') : '' }); setError(''); setNouveau(''); } }, [open, param]);
  if (!f) return null;
  const ajouter = () => { const p = nouveau.trim(); if (p && !f.postes.includes(p)) setF({ ...f, postes: [...f.postes, p] }); setNouveau(''); };
  const prod = (p) => setF({ ...f, postes_production: f.postes_production.includes(p) ? f.postes_production.filter((x) => x !== p) : [...f.postes_production, p] });
  const save = async () => {
    setError('');
    try { const r = await planningAPI.parametresPersonnel(f); onSaved(r.data.message); } catch (e) { setError(apiError(e)); }
  };
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Réglages du planning du personnel</DialogTitle>
      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Typography sx={{ fontWeight: 700, fontSize: 14, mb: 1 }}>Postes</Typography>
        <Typography sx={{ fontSize: 12.5, color: 'text.secondary', mb: 1 }}>Cochez les postes de production : ce sont eux qui comptent pour couvrir le besoin en main-d'œuvre.</Typography>
        {f.postes.map((p) => (
          <Box key={p} sx={{ display: 'flex', alignItems: 'center' }}>
            <FormControlLabel sx={{ flex: 1 }} control={<Checkbox size="small" checked={f.postes_production.includes(p)} onChange={() => prod(p)} />} label={p} />
            <Button size="small" color="error" onClick={() => setF({ ...f, postes: f.postes.filter((x) => x !== p), postes_production: f.postes_production.filter((x) => x !== p) })}>Retirer</Button>
          </Box>
        ))}
        <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
          <TextField size="small" label="Nouveau poste" value={nouveau} onChange={(e) => setNouveau(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && ajouter()} sx={{ flex: 1 }} />
          <Button startIcon={<AddOutlined />} onClick={ajouter} disabled={!nouveau.trim()}>Ajouter</Button>
        </Box>
        <TextField sx={{ mt: 3 }} fullWidth label="Productivité : poteaux par ouvrier de production et par jour" value={f.productivite}
          onChange={(e) => setF({ ...f, productivite: e.target.value })} inputProps={{ inputMode: 'decimal' }}
          helperText="Besoin du jour = poteaux planifiés ÷ productivité (arrondi au supérieur). Ex. 60 poteaux, productivité 5 → 12 ouvriers." />
      </DialogContent>
      <DialogActions><Button onClick={onClose}>Annuler</Button><Button variant="contained" onClick={save}>Enregistrer</Button></DialogActions>
    </Dialog>
  );
}

const COULEUR = { CONGE: '#7A4FB4', CONGE_DEMANDE: '#9A8CB0', ABSENCE: '#B4232A' };

const Personnel = ({ onInfo, responsable }) => {
  const [debut, setDebut] = useState(lundiDe(aujourdhui()));
  const [d, setD] = useState(null);
  const [saisie, setSaisie] = useState({});
  const [q, setQ] = useState('');
  const [reglages, setReglages] = useState(false);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const charger = useCallback(() => planningAPI.personnel(debut).then((r) => {
    setD(r.data);
    setSaisie(Object.fromEntries(r.data.affectations.map((a) => [`${a.date}|${a.employe_id}`, a.poste])));
  }).catch((e) => setError(apiError(e))), [debut]);
  useEffect(() => { charger(); }, [charger]);

  const initial = useMemo(() => Object.fromEntries((d?.affectations || []).map((a) => [`${a.date}|${a.employe_id}`, a.poste])), [d]);
  const modifs = Object.keys({ ...saisie, ...initial }).filter((k) => (saisie[k] || '') !== (initial[k] || ''));
  const indispo = useMemo(() => Object.fromEntries((d?.indisponibilites || []).map((x) => [`${x.date}|${x.employe_id}`, x])), [d]);
  const presence = useMemo(() => Object.fromEntries((d?.presences || []).map((x) => [`${x.date}|${x.employe_id}`, x])), [d]);
  const t = aujourdhui();
  const employes = (d?.employes || []).filter((e) => !q || `${e.prenom} ${e.nom} ${e.matricule} ${e.poste || ''} ${e.service || ''}`.toLowerCase().includes(q.toLowerCase()));
  // Résumé recalculé avec la saisie en cours
  const resume = (d?.resume || []).map((r) => {
    const aff = d.employes.filter((e) => saisie[`${r.date}|${e.id}`]);
    return { ...r, affectes: aff.length, affectes_production: aff.filter((e) => d.param.postes_production.includes(saisie[`${r.date}|${e.id}`])).length };
  });

  const enregistrer = async () => {
    setBusy(true); setError('');
    try {
      const r = await planningAPI.enregistrerPersonnel(modifs.map((k) => { const [date, employe_id] = k.split('|'); return { date, employe_id: Number(employe_id), poste: saisie[k] || null }; }));
      if (r.data.conflits?.length) setError(r.data.message); else onInfo(r.data.message);
      charger();
    } catch (e) { setError(apiError(e)); } finally { setBusy(false); }
  };
  const copier = async () => {
    if (modifs.length && !window.confirm('Des modifications ne sont pas enregistrées. Recopier quand même ?')) return;
    try { const r = await planningAPI.copierPersonnel(debut); onInfo(r.data.message); charger(); } catch (e) { setError(apiError(e)); }
  };
  const exporter = async () => {
    const lignes = [
      ['Matricule', 'Employé', 'Poste habituel', ...d.jours.map((j) => ({ value: new Date(`${j}T00:00:00Z`), type: Date }))],
      ...d.employes.map((e) => [e.matricule, `${e.prenom} ${e.nom}`, e.poste || '', ...d.jours.map((j) => saisie[`${j}|${e.id}`] || indispo[`${j}|${e.id}`]?.libelle || '')]),
      [],
      ['', 'Poteaux planifiés', '', ...resume.map((r) => r.production_prevue || null)],
      ['', 'Besoin (ouvriers)', '', ...resume.map((r) => r.besoin || null)],
      ['', 'Affectés production', '', ...resume.map((r) => r.affectes_production)],
    ];
    await ecrireExcel(lignes, `planning-personnel-${d.debut}.xlsx`, { largeurs: [12, 26, 18, ...d.jours.map(() => 14)] });
  };

  return (
    <>
      {error && <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {d && (!d.param.postes.length || !d.param.productivite) && (
        <Alert severity="info" sx={{ mb: 2 }} action={responsable && <Button color="inherit" onClick={() => setReglages(true)}>Régler</Button>}>
          {!d.param.postes.length ? 'Aucun poste défini : ajoutez vos postes (ferraillage, coulage…) dans les réglages du planning. ' : ''}
          {!d.param.productivite ? 'Productivité non renseignée : le besoin en main-d\'œuvre n\'est pas calculé.' : ''}
        </Alert>
      )}
      <Panel flush>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1.5, borderBottom: '1px solid', borderColor: 'divider', flexWrap: 'wrap' }}>
          <IconButton aria-label="Semaine précédente" onClick={() => setDebut(plusJours(debut, -7))}><ChevronLeft /></IconButton>
          <Typography sx={{ fontWeight: 700, minWidth: 210, textAlign: 'center' }}>{semaineLabel(debut)}</Typography>
          <IconButton aria-label="Semaine suivante" onClick={() => setDebut(plusJours(debut, 7))}><ChevronRight /></IconButton>
          <Button size="small" startIcon={<TodayOutlined />} onClick={() => setDebut(lundiDe(aujourdhui()))}>Cette semaine</Button>
          <TextField size="small" placeholder="Rechercher un employé" value={q} onChange={(e) => setQ(e.target.value)} sx={{ width: 210 }}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchOutlined fontSize="small" /></InputAdornment> }} />
          <Box sx={{ flex: 1 }} />
          <Button startIcon={<ContentCopyOutlined />} onClick={copier}>Recopier la semaine précédente</Button>
          {d && <Button startIcon={<FileDownloadOutlined />} onClick={() => exporter().catch((e) => setError(e.message))}>Excel</Button>}
          {responsable && <Tooltip title="Postes et productivité"><IconButton aria-label="Réglages du planning du personnel" onClick={() => setReglages(true)}><SettingsOutlined /></IconButton></Tooltip>}
          <Button variant="contained" startIcon={<SaveOutlined />} disabled={busy || !modifs.length} onClick={enregistrer}>Enregistrer{modifs.length ? ` (${modifs.length})` : ''}</Button>
        </Box>
        <TableContainer sx={{ maxHeight: '70vh' }}>
          <Table size="small" stickyHeader sx={{ '& td, & th': { px: 1 } }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ minWidth: 190 }}>Employé</TableCell>
                {resume.map((r) => (
                  <TableCell key={r.date} align="center" sx={{ minWidth: 118, bgcolor: r.date === t ? 'action.selected' : undefined, color: !r.travaille || r.ferie ? 'text.disabled' : undefined }}>
                    {jourCourt(r.date)}{r.ferie && <Typography sx={{ fontSize: 11 }}>{r.ferie}</Typography>}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {d && [
                ['Poteaux planifiés', (r) => r.production_prevue || '—'],
                ['Besoin (ouvriers)', (r) => r.besoin || '—'],
                ['Affectés production', (r) => <Box component="span" sx={{ color: r.besoin && r.affectes_production < r.besoin ? 'error.main' : r.besoin ? 'success.main' : undefined, fontWeight: 700 }}>{r.affectes_production}{r.besoin && r.affectes_production < r.besoin ? ` (−${r.besoin - r.affectes_production})` : ''}</Box>],
                ['Disponibles · absents', (r) => `${r.disponibles} · ${r.indisponibles}`],
                ['Présents (pointage)', (r) => (r.presents === null ? '—' : r.presents)],
              ].map(([l, f]) => (
                <TableRow key={l} sx={{ bgcolor: 'action.hover' }}>
                  <TableCell sx={{ fontSize: 12.5, fontWeight: 600 }}>{l}</TableCell>
                  {resume.map((r) => <TableCell key={r.date} align="center" sx={{ fontFamily: tokens.mono, fontSize: 13 }}>{f(r)}</TableCell>)}
                </TableRow>
              ))}
              {employes.map((e) => (
                <TableRow key={e.id} hover>
                  <TableCell><Typography sx={{ fontSize: 13.5, fontWeight: 600 }}>{e.prenom} {e.nom}</Typography>
                    <Typography sx={{ fontSize: 11.5, color: 'text.secondary' }}>{[e.matricule, e.poste].filter(Boolean).join(' · ')}</Typography></TableCell>
                  {d.jours.map((j) => {
                    const k = `${j}|${e.id}`; const ind = indispo[k]; const pr = presence[k]; const val = saisie[k] || '';
                    const passe = j <= t;
                    return (
                      <TableCell key={j} align="center" sx={{ bgcolor: j === t ? 'action.selected' : undefined }}>
                        {ind && <Typography sx={{ fontSize: 11, fontWeight: 700, color: COULEUR[ind.type] }}>{ind.libelle}</Typography>}
                        <TextField select variant="standard" value={val} SelectProps={{ native: true }} sx={{ width: 108, '& select': { fontSize: 13 } }}
                          inputProps={{ 'aria-label': `${e.prenom} ${e.nom} ${jourCourt(j)}` }} error={!!(val && ind && ind.type !== 'CONGE_DEMANDE')}
                          onChange={(ev) => setSaisie((x) => ({ ...x, [k]: ev.target.value }))}>
                          <option value="">—</option>
                          {d.param.postes.map((p) => <option key={p} value={p}>{p}</option>)}
                          {val && !d.param.postes.includes(val) && <option value={val}>{val}</option>}
                        </TextField>
                        {passe && (pr?.arrivee
                          ? <Typography sx={{ fontSize: 11, color: 'success.main' }}>présent {pr.arrivee}</Typography>
                          : initial[k] && j < t && <Typography sx={{ fontSize: 11, color: 'error.main' }}>pas de pointage</Typography>)}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))}
              {d && !employes.length && <TableRow><TableCell colSpan={8} sx={{ color: 'text.secondary', py: 3, textAlign: 'center' }}>{d.employes.length ? 'Aucun employé ne correspond.' : 'Aucun employé actif : ajoutez le personnel dans Ressources humaines → Employés.'}</TableCell></TableRow>}
            </TableBody>
          </Table>
        </TableContainer>
      </Panel>
      <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: 'wrap', gap: 1 }}>
        <Chip size="small" label="Congé approuvé" sx={{ color: COULEUR.CONGE }} variant="outlined" />
        <Chip size="small" label="Congé demandé (non encore approuvé)" sx={{ color: COULEUR.CONGE_DEMANDE }} variant="outlined" />
        <Chip size="small" label="Absence" sx={{ color: COULEUR.ABSENCE }} variant="outlined" />
        <Typography sx={{ fontSize: 12.5, color: 'text.secondary' }}>Les congés et absences viennent des Ressources humaines ; la présence, du pointage. « — » = non affecté / repos.</Typography>
      </Stack>
      <ReglagesDialog open={reglages} param={d?.param} onClose={() => setReglages(false)} onSaved={(m) => { setReglages(false); onInfo(m); charger(); }} />
    </>
  );
};

export default Personnel;
